import { useCallback, useRef, useState } from 'react';

/**
 * Snapshot-based undo/redo stack.
 *
 * The hook stores raw snapshots of whatever shape `T` is — the caller owns
 * "what counts as a state change". A `push(snapshot)` records the snapshot
 * onto the undo stack AND clears the redo stack (a new branch invalidates
 * any future history). Stacks evict the oldest entry once they hit
 * `maxDepth`.
 *
 * `undo(currentBeforeUndo)` returns the previous snapshot and slides the
 * current one onto the redo stack. `redo(currentBeforeRedo)` does the
 * inverse. Both return `null` when their respective stack is empty.
 *
 * The hook itself stays pure — no React state for the stacks (they live
 * in refs to avoid re-rendering on every push). `canUndo` / `canRedo`
 * however ARE backed by state so consumers re-render the toolbar's
 * enabled/disabled affordances when the stacks change.
 */
export interface UseWorkflowUndoResult<T> {
  push: (snapshot: T) => void;
  undo: (currentBeforeUndo: T) => T | null;
  redo: (currentBeforeRedo: T) => T | null;
  canUndo: boolean;
  canRedo: boolean;
  clear: () => void;
}

const DEFAULT_MAX_DEPTH = 50;

export const useWorkflowUndo = <T>(
  maxDepth: number = DEFAULT_MAX_DEPTH,
): UseWorkflowUndoResult<T> => {
  const undoStackRef = useRef<T[]>([]);
  const redoStackRef = useRef<T[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const sync = useCallback(() => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  const push = useCallback(
    (snapshot: T) => {
      const trimmed = undoStackRef.current.slice(-(maxDepth - 1));
      trimmed.push(snapshot);
      undoStackRef.current = trimmed;
      redoStackRef.current = []; // new branch — drop any pending redo
      sync();
    },
    [maxDepth, sync],
  );

  const undo = useCallback(
    (currentBeforeUndo: T): T | null => {
      const prev = undoStackRef.current.pop();
      if (prev === undefined) {
        sync();
        return null;
      }

      const trimmedRedo = redoStackRef.current.slice(-(maxDepth - 1));
      trimmedRedo.push(currentBeforeUndo);
      redoStackRef.current = trimmedRedo;
      sync();
      return prev;
    },
    [maxDepth, sync],
  );

  const redo = useCallback(
    (currentBeforeRedo: T): T | null => {
      const next = redoStackRef.current.pop();
      if (next === undefined) {
        sync();
        return null;
      }

      const trimmedUndo = undoStackRef.current.slice(-(maxDepth - 1));
      trimmedUndo.push(currentBeforeRedo);
      undoStackRef.current = trimmedUndo;
      sync();
      return next;
    },
    [maxDepth, sync],
  );

  const clear = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    sync();
  }, [sync]);

  return { push, undo, redo, canUndo, canRedo, clear };
};
